/**
 * Kai vocabulary — the capability manifest.
 *
 * Answers "what actually exists in this user's Nautilus?" so that both the
 * deterministic parser and the LLM can resolve names against real data instead
 * of inventing them.
 *
 * SECURITY / PRIVACY CONTRACT: serializeKaiVocabularyForLLM() is the only thing
 * ever sent to the model. It carries tag names, project names and the static
 * enums — never task titles, descriptions, dates or any task row. The unit test
 * tests/unit/kaiVocabulary.test.js enforces this.
 *
 * @module kaiVocabulary
 */

import { VALID_STATUSES, VALID_PRIORITIES } from '../config/constants.js';
import {
    KAI_DATE_PRESETS,
    KAI_LINK_TYPES,
    KAI_UPDATED_FILTERS,
    KAI_PROJECT_STATUSES,
    KAI_PROJECT_TASK_FILTERS
} from '../../utils/kai-query-shared.js';

/** Dimensions a user may plausibly ask for that Nautilus cannot express. */
export const KAI_UNSUPPORTED_DIMENSIONS = [
    'assignee', 'owner', 'estimated effort', 'story points', 'milestone', 'sprint',
    'epic', 'workspace', 'category', 'subtask', 'parent task', 'created date',
    'completion date', 'sort', 'limit', 'group by'
];

/**
 * Build the manifest from live in-memory data.
 *
 * @param {Object} options
 * @param {Array}  options.tasks - the tasks array (read-only; never leaves the client)
 * @param {Array}  options.projects - the projects array
 * @param {boolean} options.reviewStatusEnabled - settings.enableReviewStatus
 * @param {string} options.today - ISO date in the user's timezone
 * @param {string} options.timezone
 * @param {string} options.locale - 'en' | 'es'
 */
export function buildKaiVocabulary({
    tasks = [],
    projects = [],
    reviewStatusEnabled = false,
    today = '',
    timezone = '',
    locale = 'en'
} = {}) {
    // Templates and archived rows are invisible in every normal view — a tag that
    // only exists on them is not something the user can filter to.
    const liveTasks = tasks.filter(task => task && !task.isTemplate && !task.archivedAt);

    const tagCounts = new Map();
    let hasUntaggedTasks = false;
    let hasProjectlessTasks = false;
    for (const task of liveTasks) {
        const tags = Array.isArray(task.tags) ? task.tags : [];
        if (tags.length === 0) hasUntaggedTasks = true;
        for (const tag of tags) {
            const name = String(tag || '').trim();
            if (!name) continue;
            tagCounts.set(name, (tagCounts.get(name) || 0) + 1);
        }
        if (!task.projectId) hasProjectlessTasks = true;
    }

    const projectTagCounts = new Map();
    const liveProjects = projects.filter(project => project && !project.archivedAt);
    for (const project of liveProjects) {
        for (const tag of (Array.isArray(project.tags) ? project.tags : [])) {
            const name = String(tag || '').trim();
            if (!name) continue;
            projectTagCounts.set(name, (projectTagCounts.get(name) || 0) + 1);
        }
    }

    // `review` is hidden behind a setting. If it's off there is no checkbox for
    // it, so a query that selects it would be un-removable from the toolbar.
    const statuses = VALID_STATUSES.filter(status => status !== 'review' || reviewStatusEnabled);

    return {
        today,
        timezone,
        locale,
        statuses,
        priorities: [...VALID_PRIORITIES],
        datePresets: [...KAI_DATE_PRESETS],
        linkTypes: [...KAI_LINK_TYPES],
        updatedFilters: [...KAI_UPDATED_FILTERS],
        projectStatuses: [...KAI_PROJECT_STATUSES],
        projectTaskFilters: KAI_PROJECT_TASK_FILTERS.filter(Boolean),
        tags: sortByCountThenName(tagCounts),
        projectTags: sortByCountThenName(projectTagCounts),
        projects: liveProjects.map(project => ({ id: String(project.id), name: String(project.name || '') })),
        hasUntaggedTasks,
        hasProjectlessTasks,
        archivedTaskCount: tasks.filter(task => task && task.archivedAt && !task.isTemplate).length,
        archivedProjectCount: projects.filter(project => project && project.archivedAt).length,
        unsupportedDimensions: [...KAI_UNSUPPORTED_DIMENSIONS]
    };
}

function sortByCountThenName(counts) {
    return [...counts.entries()]
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

/**
 * Reduce the manifest to the payload sent to the LLM.
 *
 * Capped so a user with thousands of tags cannot blow the context window. The
 * *local* parser always sees the full vocabulary — only the model's copy is
 * truncated, and `truncated` tells the model to admit uncertainty rather than
 * guess at a name it wasn't shown.
 */
export function serializeKaiVocabularyForLLM(vocabulary, { maxTags = 120, maxProjects = 80 } = {}) {
    const vocab = vocabulary || {};
    const tags = (vocab.tags || []).slice(0, maxTags).map(tag => tag.name);
    const projects = (vocab.projects || []).slice(0, maxProjects).map(project => ({
        id: project.id,
        name: project.name
    }));

    return {
        today: vocab.today || '',
        timezone: vocab.timezone || '',
        locale: vocab.locale || 'en',
        statuses: vocab.statuses || [],
        priorities: vocab.priorities || [],
        datePresets: vocab.datePresets || [],
        linkTypes: vocab.linkTypes || [],
        projectStatuses: vocab.projectStatuses || [],
        tags,
        projects,
        hasUntaggedTasks: !!vocab.hasUntaggedTasks,
        hasProjectlessTasks: !!vocab.hasProjectlessTasks,
        unsupportedDimensions: vocab.unsupportedDimensions || [],
        truncated: (vocab.tags || []).length > maxTags || (vocab.projects || []).length > maxProjects
    };
}
